import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { Button, Typography } from 'components/common'
import {
  Thread,
  useCreateThreadMutation,
  useThreadFileGetURLQueryLazyQuery,
  useThreadFileUploadURLQueryLazyQuery,
  useUpdateThreadMutation
} from 'graphql/generated'
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import ModalTrigger from 'components/common/ModalTrigger'
import BaseModal from 'components/modals/BaseModal'
import TextField from 'components/common/TextField'
import { useCollectiveContext } from 'context/CollectiveContext'
import FileUploads from './FileUploads'
import dynamic from 'next/dynamic'
import useS3FileUpload from './useS3FileUpload'
import { IProps } from 'components/QuillNoSSR'
import ReactQuill from 'react-quill'
import FieldLabel from 'components/common/FieldLabel'
import { FileWrapper } from './FileUpload'
import Image from 'next/image'
import {
  faFileDownload,
  faPencil,
  faTimes
} from '@fortawesome/pro-regular-svg-icons'
import Spinner from 'components/Spinner'
import { useForumContext } from './ForumContext'
import { CategoryThreadsQuery, ThreadQuery } from 'graphql/documents'
import { LIMIT } from './useThreadComments'
import { useRouter } from 'next/router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import convertImageUrlsToS3Key from './convertImageUrlsToS3Key'
import useConvertS3KeysToImageUrl from './useConvertS3KeysToImageUrls'
import { useAnalyticsContext } from 'context/AnalyticsContext'

type TWrappedComponent = {
  forwardedRef: RefObject<ReactQuill>
} & IProps

const QuillNoSSRWrapper = dynamic(
  async () => {
    const { default: Quill } = await import('components/QuillNoSSR')
    return ({ forwardedRef, ...props }: TWrappedComponent) => {
      const quill = useMemo(() => {
        return <Quill ref={forwardedRef} {...props} advancedButtons />
      }, [forwardedRef, props])
      return quill
    }
  },
  { ssr: false }
)

const useThreadInputModal = ({
  thread
}: {
  thread?: Pick<Thread, 'id' | 'title' | 'attachments' | 'body'>
}) => {
  const router = useRouter()
  const { collective } = useCollectiveContext()
  const { discussionBoard, selectedCategory } = useForumContext()
  const quillRef = useRef<ReactQuill>(null)
  const { loaded: analyticsLoaded } = useAnalyticsContext()

  const [title, setTitle] = useState<string>('')
  const [loadingBody, setLoadingBody] = useState<boolean>(false)
  const [body, setBody] = useState<string>()
  const [uploadingImage, setUploadingImage] = useState<boolean>(false)
  const [attachments, setAttachments] = useState<
    Array<{
      id: string
      type: string
      url: string
      name: string
    }>
  >([])
  const [showModal, setShowModal] = useState<boolean>(false)

  const [newAttachments, setNewAttachments] = useState<
    Array<{ key: string; type: string; name: string }>
  >([])

  const updateUrls = useConvertS3KeysToImageUrl(collective.discussionBoard.id)

  useEffect(() => {
    async function parseBodyUrl() {
      if (thread && showModal) {
        setLoadingBody(true)
        setBody(await updateUrls(thread.body))
        setLoadingBody(false)
      }
    }
    parseBodyUrl()
    if (thread) {
      setTitle(thread.title)
      setAttachments(thread.attachments)
    }
  }, [showModal, thread, updateUrls])

  const [createThread, { loading: isCreating }] = useCreateThreadMutation()
  const [updateThread, { loading: isUpdating }] = useUpdateThreadMutation()

  const canSave =
    !!title.trim().length &&
    !!body?.trim().length &&
    !isCreating &&
    !isUpdating &&
    body.trim() !== '<p><br></p>'

  const isEditing = !!thread

  const save = useCallback(async () => {
    if (!canSave) {
      return
    }

    if (isEditing) {
      await updateThread({
        variables: {
          threadId: thread.id,
          threadDetails: {
            body: convertImageUrlsToS3Key(body),
            title,
            existingAttachments: attachments.map(at => at.id),
            newAttachments
          }
        },
        refetchQueries: [
          {
            query: ThreadQuery,
            variables: {
              threadId: thread.id,
              commentConnection: {
                first: LIMIT
              },
              repliesConnection: {
                first: 5
              },
              collaboratorConnection: {
                first: 5
              }
            }
          }
        ],
        onCompleted: () => {
          if (analyticsLoaded) {
            window.analytics?.trackAll(`Forum - Thread - Edit`, {
              threadId: thread.id,
              threadTitle: thread.title.trim()
            })
          }
        }
      })
    } else {
      await createThread({
        variables: {
          categoryId: selectedCategory.id,
          threadDetails: {
            body: convertImageUrlsToS3Key(body),
            title,
            attachments: newAttachments
          }
        },
        onCompleted: ({ createThread }) => {
          if (analyticsLoaded) {
            window.analytics?.trackAll(`Forum - Thread - Created`, {
              threadTitle: title.trim()
            })
          }
          router.push(createThread.publicUrl)
        },
        refetchQueries: [
          {
            query: CategoryThreadsQuery,
            variables: {
              categoryId: selectedCategory.id,
              threadConnection: {
                first: LIMIT
              },
              collaboratorConnection: {
                first: 5
              }
            }
          }
        ]
      })
    }
    setNewAttachments([])
    setShowModal(false)
  }, [
    analyticsLoaded,
    attachments,
    body,
    canSave,
    createThread,
    isEditing,
    newAttachments,
    router,
    selectedCategory.id,
    thread,
    title,
    updateThread
  ])

  const [getUploadUrl] = useThreadFileUploadURLQueryLazyQuery()
  const [getFileUrl] = useThreadFileGetURLQueryLazyQuery()

  const { upload, uploadStatus } = useS3FileUpload()

  const imageUpload = useCallback(
    async (file: File) => {
      setUploadingImage(true)

      const editor = quillRef.current?.getEditor()
      if (!editor) {
        return
      }
      const inputIndex = editor.getSelection()?.index || 0

      const { data: uploadUrl } = await getUploadUrl({
        variables: {
          discussionBoardId: discussionBoard.id,
          fileName: encodeURIComponent(file.name.trim()).replaceAll(
            /%../gm,
            '-'
          ),
          fileType: file.type
        }
      })

      if (!uploadUrl) {
        return
      }
      const key = uploadUrl.threadFileUploadUrl.key
      await upload(file, uploadUrl.threadFileUploadUrl.url, key)

      const { data } = await getFileUrl({
        variables: {
          discussionBoardId: discussionBoard.id,
          key
        }
      })
      editor.insertEmbed(inputIndex, 'image', data?.threadFileGetUrl.url)
      setUploadingImage(false)
      if (analyticsLoaded) {
        window.analytics?.trackAll(`Forum - Thread - Image Upload`, {
          key
        })
      }
    },
    [analyticsLoaded, discussionBoard.id, getFileUrl, getUploadUrl, upload]
  )

  const modalContent = (
    <BaseModal
      classNames="md:min-w-md sm:max-w-2xl"
      disableBackdropClick
      onClose={() => {
        setShowModal(false)
      }}
    >
      <div className="flex flex-col py-4 flex-1 overflow-auto">
        <Typography size="heading" className="px-4 pb-2">
          {isEditing ? `Editing: ${thread.title}` : 'New Thread'}
        </Typography>

        <div className="flex flex-col flex-1 overflow-auto p-4">
          <div className="flex flex-1 flex-col gap-4">
            <TextField
              label="Name"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Alpha Matrix"
              required
            />

            <div>
              <FieldLabel label="Thread Content" required />
              <div className="relative">
                {thread && loadingBody ? (
                  <div className="w-full h-48 flex justify-center">
                    <Spinner />
                  </div>
                ) : (
                  <QuillNoSSRWrapper
                    forwardedRef={quillRef}
                    detailsHtml={body || ''}
                    setDetailsHtml={setBody}
                    placeholder="Enter body for your thread here"
                    className="focus-within:border-blue-500"
                    imageHandler={() => {
                      const input = document.createElement('input')

                      input.setAttribute('type', 'file')
                      input.setAttribute('accept', 'image/*')
                      input.click()

                      input.onchange = async () => {
                        if (!input.files) {
                          return
                        }
                        const file: any = input.files[0]
                        await imageUpload(file)
                      }
                    }}
                  />
                )}

                {uploadingImage && (
                  <div className="absolute w-full h-full inset-0 flex flex-col px-4 justify-center items-center bg-gray-900/70">
                    <div className="text-center text-sm text-white">
                      Uploading {uploadStatus.name}
                    </div>

                    <div className="w-full rounded-full bg-gray-700 mt-4">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{
                          width: `${uploadStatus.progress}%`
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              {!!attachments.length && <FieldLabel label="Attachments" />}

              <div className="flex-1 relative mt-1 flex flex-wrap gap-4">
                <FileUploads
                  discussionBoardId={collective.discussionBoard.id}
                  onFilesReady={fileData => {
                    setNewAttachments(fileData)
                  }}
                />

                {attachments.map((attachment, i) => {
                  if (attachment.type.includes('image')) {
                    return (
                      <FileWrapper key={attachment.id}>
                        <Image
                          className="border border-gray-200 rounded-md object-cover"
                          src={
                            attachment.url.startsWith('/') ||
                            attachment.url.startsWith('http')
                              ? attachment.url
                              : `/${attachment.url}`
                          }
                          alt={attachment.url}
                          fill
                        />
                        <div
                          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center cursor-pointer shadow-sm border border-gray-300 text-white"
                          onClick={() => {
                            const attachmentsCopy = [...attachments]
                            attachmentsCopy.splice(i, 1)
                            setAttachments(attachmentsCopy)
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} size="sm" />
                        </div>
                      </FileWrapper>
                    )
                  }

                  return (
                    <FileWrapper
                      key={attachment.id}
                      size="md"
                      className="border border-gray-200 bg-gray-200 px-2 space-y-2 hover:border-gray-300 hover:bg-gray-300"
                    >
                      <Typography
                        href={attachment.url}
                        newTab
                        color="gray"
                        className="p-2 w-full h-full flex items-center justify-center gap-2 break-words"
                      >
                        <FontAwesomeIcon icon={faFileDownload} />
                        {` ${attachment.name}`}
                      </Typography>

                      <div
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center cursor-pointer shadow-sm border border-gray-300 text-white"
                        onClick={() => {
                          const attachmentsCopy = [...attachments]
                          attachmentsCopy.splice(i, 1)
                          setAttachments(attachmentsCopy)
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} size="sm" />
                      </div>
                    </FileWrapper>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 pt-2 flex justify-end border-t border-gray-200">
          <Button
            label="Save Thread"
            loading={isCreating || isUpdating}
            disabled={!canSave}
            onClick={save}
          />
        </div>
      </div>
    </BaseModal>
  )

  return {
    modalTrigger: (
      <ModalTrigger
        trigger={show => (
          <Button
            icon={isEditing ? faPencil : faPlus}
            label={isEditing ? 'Edit' : 'New Thread'}
            color={isEditing ? 'outline' : 'blue'}
            onClick={show}
            size="xs"
          />
        )}
      >
        {modalContent}
      </ModalTrigger>
    ),
    modalContent,
    showModal,
    setShowModal
  }
}

export default useThreadInputModal
